import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLoadScript } from "@react-google-maps/api";

const libraries: any = ['places'];

function useNewPlacesAutocomplete(selectedCountry: string) {
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const sessionTokenRef = useRef<any>(null);

    const initToken = () => {
        if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    };

    const fetchSuggestions = useCallback(async (input: string) => {
        setValue(input);
        if (!input) {
            setSuggestions([]);
            return;
        }

        if (!window.google?.maps?.places?.AutocompleteSuggestion) {
            return;
        }

        initToken();
        setLoading(true);

        try {
            const request: any = {
                input,
                sessionToken: sessionTokenRef.current,
            };
            
            if (selectedCountry) {
                request.includedRegionCodes = [selectedCountry.toLowerCase()];
            }

            const response = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            setSuggestions(response.suggestions || []);
        } catch (error) {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [selectedCountry]);

    const clearSuggestions = useCallback(() => {
        setSuggestions([]);
    }, []);

    return { value, setValue, suggestions, fetchSuggestions, clearSuggestions, loading };
}

export interface DynamicFormField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  defaultValue?: any;
  options?: string[];
  showIf?: {
    field: string;
    equals: any;
  };
  placeholder?: string;
}

export interface DynamicFormStepConfig {
  title?: string;
  instructionsHtml?: string;
  fields?: DynamicFormField[];
}

interface DynamicFormCardProps {
  config: DynamicFormStepConfig;
  onBack?: () => void;
  onContinue: (data: any) => void;
  initialData?: any;
}

const DynamicFormCard: React.FC<DynamicFormCardProps> = ({ config, onBack, onContinue, initialData = {} }) => {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);

  const { isLoaded } = useLoadScript({
      googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '',
      libraries,
      version: "weekly"
  });

  const {
      value: addressValue,
      setValue: setAddressValue,
      suggestions: addressSuggestions,
      fetchSuggestions: fetchAddressSuggestions,
      clearSuggestions: clearAddressSuggestions,
  } = useNewPlacesAutocomplete('US');

  const [activeAddressField, setActiveAddressField] = useState<string | null>(null);

  const handleAddressSelect = async (suggestion: any, key: string) => {
      const formatted = (suggestion.placePrediction?.text?.text || '').replace(/,\s*USA$/, '').replace(/\s*USA$/, '').trim();
      setAddressValue(formatted);
      handleChange(key, formatted);
      clearAddressSuggestions();
      setActiveAddressField(null);
  };

  const handleChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const getMissingFields = () => {
    if (!config || !config.fields) return [];
    const missing: string[] = [];
    for (const field of config.fields) {
      if (field.showIf) {
        const dependentValue = formData[field.showIf.field];
        if (dependentValue !== field.showIf.equals) {
          continue;
        }
      }
      if (field.required && (formData[field.key] === undefined || formData[field.key] === null || formData[field.key] === '')) {
        missing.push(field.label);
      }
    }
    return missing;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (getMissingFields().length === 0) {
      onContinue(formData);
    }
  };

  const renderField = (field: DynamicFormField) => {
    if (field.showIf) {
      const dependentValue = formData[field.showIf.field];
      if (dependentValue !== field.showIf.equals) return null;
    }

    const value = formData[field.key] !== undefined ? formData[field.key] : (field.defaultValue !== undefined ? field.defaultValue : '');

    let inputElement = null;

    switch (field.type) {
      case 'boolean':
        inputElement = (
          <div className="flex items-center gap-2 mt-2 mb-4">
            <input
              type="checkbox"
              id={field.key}
              checked={!!value}
              onChange={(e) => handleChange(field.key, e.target.checked)}
              className="w-5 h-5 text-[#0B4BB6] border-gray-300 rounded focus:ring-[#0B4BB6]"
            />
            <label htmlFor={field.key} className="text-[#141435] font-plus-jakarta">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          </div>
        );
        break;
      case 'select':
        inputElement = (
          <div className="mb-4">
            <label className="block text-sm font-plus-jakarta font-semibold text-[#141435] mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleChange(field.key, e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6]"
            >
              <option value="">Select an option</option>
              {field.options?.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );
        break;
      case 'textarea':
        inputElement = (
          <div className="mb-4">
            <label className="block text-sm font-plus-jakarta font-semibold text-[#141435] mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleChange(field.key, e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6] min-h-[100px]"
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            />
          </div>
        );
        break;
      case 'address':
        inputElement = (
          <div className="mb-4 relative">
            <label className="block text-sm font-plus-jakarta font-semibold text-[#141435] mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={activeAddressField === field.key ? addressValue : value}
              onChange={(e) => {
                setActiveAddressField(field.key);
                fetchAddressSuggestions(e.target.value);
                handleChange(field.key, e.target.value);
              }}
              onFocus={(e) => {
                setActiveAddressField(field.key);
                setAddressValue(value);
              }}
              onBlur={() => {
                // small delay to allow click on suggestion
                setTimeout(() => {
                  if (activeAddressField === field.key) {
                    setActiveAddressField(null);
                    clearAddressSuggestions();
                  }
                }, 200);
              }}
              className="w-full px-4 py-3 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6]"
              placeholder={field.placeholder || "Search or enter full address"}
            />
            {activeAddressField === field.key && addressSuggestions.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border border-[#D7DFEC] rounded shadow-lg mt-1 max-h-60 overflow-auto">
                {addressSuggestions.map((suggestion: any, index: number) => (
                  <li
                    key={index}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-[14px] text-[#141435]"
                    onMouseDown={e => {
                      e.preventDefault();
                      handleAddressSelect(suggestion, field.key);
                    }}
                  >
                    {suggestion.placePrediction.text.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
        break;
      case 'html':
        inputElement = (
          <div className="mb-6 mt-2 text-[14px] text-gray-700 font-plus-jakarta" dangerouslySetInnerHTML={{ __html: field.defaultValue || field.placeholder || '' }} />
        );
        break;
      default:
        inputElement = (
          <div className="mb-4">
            <label className="block text-sm font-plus-jakarta font-semibold text-[#141435] mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type={field.type === 'email' ? 'email' : 'text'}
              value={value}
              onChange={(e) => handleChange(field.key, e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6]"
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            />
          </div>
        );
    }

    return (
      <div key={field.key}>
        {inputElement}
      </div>
    );
  };

  const missingFields = getMissingFields();

  return (
    <div className="w-full">
      {config?.instructionsHtml && (
        <div 
          className="mb-6 text-[15px] font-plus-jakarta text-gray-700 bg-gray-50 p-4 rounded-lg border border-[#D7DFEC]"
          dangerouslySetInnerHTML={{ __html: config.instructionsHtml }}
        />
      )}
      <form onSubmit={handleSubmit} className="w-full">
        {config?.fields?.map(renderField)}
        
        {missingFields.length > 0 && (
          <div className="w-full text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-2.5 flex items-start gap-2 mb-4">
            <svg className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
            <span>
              <span className="font-semibold">Required fields: </span>
              {missingFields.join(', ')}
            </span>
          </div>
        )}

        <div className="flex flex-row w-full gap-4 mt-8">
          {onBack && (
            <button
              type="button"
              className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full hover:bg-[#485D7D] transition-colors"
              onClick={onBack}
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className={`flex-1 text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full transition-colors ${missingFields.length > 0 ? 'bg-[#0B4BB6] opacity-50 cursor-not-allowed' : 'bg-[#0B4BB6] hover:bg-[#23235a]'}`}
            disabled={missingFields.length > 0}
          >
            Save & Continue
          </button>
        </div>
      </form>
    </div>
  );
};

export default DynamicFormCard;
